'use client';

import { useSearchParams } from 'next/navigation';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { FeaturedCards } from '@/app/_components/NDR/FeaturedCards';
import { FeaturedHero } from '@/app/_components/NDR/FeaturedHero';
import { NdrCompanyCard, NdrCompanyCardSkeleton } from '@/app/_components/NDR/NdrCompanyCard';
import { NdrFooter } from '@/app/_components/NDR/NdrFooter';
import { NdrHeader } from '@/app/_components/NDR/NdrHeader';
import { NdrStickyBar } from '@/app/_components/NDR/NdrStickyBar';
import { TableOfContents } from '@/app/_components/NDR/TableOfContents';
import { detectTrafficSource, getNdrHomeUrl, isNdrLink } from '@/app/_lib/utils/ndrUtils';
import { ndrData } from '@/app_data/mock/ndrData';

const howItWorksWrapperVariants = tv({
  defaultVariants: {
    source: 'google',
    variant: 'default',
  },
  slots: {
    companyCardsContainer: 'flex flex-col layout-gap-md items-start',
    companyCardsGrid: 'flex flex-col layout-gap-md items-start',
    companyCardsSection: 'flex flex-col layout-gap-default',
    container: 'ndr-page-wrapper flex flex-col layout-gap-md layout-sticky-safe-area',
    contentGrid: 'grid layout-grid-gap md:grid-cols-[2fr_1fr]',
    contentWrapper: 'w-full container-ft lg:mx-auto px-lg',
    headerSection: 'w-full layout-safe-area shadow-[4px_4px_20px_0_rgba(17,17,17,0.05)] min-h-16',
    heroSection: 'w-full container-ft lg:mx-auto px-lg',
    sectionHeader: 'flex flex-col gap-sm',
    sectionSubtitle: 'typography-level-6',
    sectionTitle: 'font-semibold typography-level-6',
    stickyToc: 'block',
    tocWrapper: 'md:sticky top-4',
  },
  variants: {
    source: {
      bing: {
        container: 'is-bing-source',
      },
      google: {
        container: 'is-google-source',
      },
    },
  },
});

interface NdrWrapperContentProps extends VariantProps<typeof howItWorksWrapperVariants> {
  children?: React.ReactNode;
  source?: 'google' | 'bing';
}

interface RetreaverCampaign {
  // biome-ignore lint/style/useNamingConvention: required by Retreaver
  auto_replace_numbers: () => void;
}

const HowItWorksWrapper = ({ children, source }: NdrWrapperContentProps) => {
  const searchParams = useSearchParams();

  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [companyCardMounted, setCompanyCardMounted] = useState(false);

  const retreaverCampaign = useRef<RetreaverCampaign | null>(null);
  const retreaverWaiting = useRef<boolean>(false);

  // Get all query parameters as a string
  const allQueryParamsString =
    searchParams?.toString() || (source === 'google' ? 'src=fortune.google.organic' : 'src=fortune.bing.organic');
  const isGoogleSource = detectTrafficSource(searchParams) === 'google' || source === 'google';
  const homeUrl = getNdrHomeUrl(isGoogleSource ? 'google' : 'bing');
  const sourceVariant = isGoogleSource ? 'google' : 'bing';
  const stickyTriggerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;

  const data = isGoogleSource ? ndrData.landingPages.google : ndrData.landingPages.bing;

  const topNumber = 5;
  const orderedReviews = data.reviews.sort((a, b) => a.ordinal - b.ordinal).slice(0, topNumber);
  const stickyBarData = data.reviews.filter((item) => item.sticky === true)[0];

  const slots = howItWorksWrapperVariants({ source: sourceVariant });

  // Initialize (once) & run Retreaver to replace phone numbers
  const runRetreaver = useCallback(() => {
    const performAction = () => {
      if (retreaverCampaign.current === null) {
        window.Retreaver.configure({
          host: 'api.calltrackapi.com',
          prefix: 'https',
        });

        retreaverCampaign.current = new window.Retreaver.Campaign({
          // biome-ignore lint/style/useNamingConvention: required by Retreaver
          campaign_key: 'c0ff9c04926762b90d3201cb7444c95b',
        }) as RetreaverCampaign;
      }

      retreaverCampaign.current.auto_replace_numbers();
    };

    if (retreaverWaiting.current) {
      return;
    }

    if (window.Retreaver === undefined) {
      const retreaveInterval = 100;
      retreaverWaiting.current = true;
      const intvl = setInterval(() => {
        if (window.Retreaver !== undefined) {
          retreaverWaiting.current = false;
          clearInterval(intvl);
          performAction();
        }
      }, retreaveInterval);
    } else {
      performAction();
    }
  }, []);

  useEffect(() => {
    if (!(stickyBarMounted && companyCardMounted)) {
      return;
    }
    runRetreaver();
  }, [stickyBarMounted, companyCardMounted, runRetreaver]);

  if (!data.featuredHero) {
    return null;
  }

  return (
    <div className={slots.container()}>
      <div className={slots.headerSection()}>
        <NdrHeader
          className='py-xs'
          disclaimerText={data.disclosure.text}
          disclaimerTitle={data.disclosure.title}
          homeUrl={homeUrl}
        />
      </div>

      <div className={slots.heroSection()}>
        <FeaturedHero
          className='lg:container-ft px-lg'
          data={data.featuredHero}
          queryParams={allQueryParamsString}
        />
      </div>

      <div className={slots.contentWrapper()}>
        <div className={slots.contentGrid()}>
          <div
            className={slots.companyCardsSection()}
            data-cy='ndr-company-cards'
          >
            <FeaturedCards data={data.featuredHero.cards} />
            <div className={slots.companyCardsContainer()}>
              <div className={slots.sectionHeader()}>
                <h3 className={slots.sectionTitle()}>{data.featuredHero.fullReviewsTitle}</h3>
                <p className={slots.sectionSubtitle()}>{data.featuredHero.fullReviewsSubtitle}</p>
              </div>
              <div className={slots.companyCardsGrid()}>
                {orderedReviews.map((review, index) => (
                  <Suspense
                    fallback={<NdrCompanyCardSkeleton isHighlighted={index === 0} />}
                    key={review.companySlug || review.companyName}
                  >
                    <NdrCompanyCard
                      card={review}
                      className='w-full'
                      isHighlighted={index === 0}
                      onMount={() => setCompanyCardMounted(true)}
                      onResize={runRetreaver}
                      queryParams={isNdrLink(review.ctas.primary.url) ? allQueryParamsString : ''}
                    />
                  </Suspense>
                ))}
              </div>
            </div>
          </div>

          <div className={slots.stickyToc()}>
            <div className={slots.tocWrapper()}>
              <TableOfContents
                queryParams={allQueryParamsString}
                reviews={orderedReviews}
                source={isGoogleSource ? 'google' : 'bing'}
              />
            </div>
          </div>
        </div>
      </div>

      <div
        className='absolute'
        ref={stickyTriggerRef}
      />
      <NdrStickyBar
        onMount={() => setStickyBarMounted(true)}
        queryParams={isNdrLink(data.primaryCta.url) ? allQueryParamsString : ''}
        review={stickyBarData}
        stickyButtonPhone={data.defaultPhoneNumber}
        triggerElement={stickyTriggerRef}
      />
      <NdrFooter footerContent={data.footer} />
      {children}
    </div>
  );
};

export { HowItWorksWrapper };
